import { useState, useEffect } from 'react'
import SubVisual from '../../components/SubVisual'
import { useTranslation } from 'react-i18next'

const Contact = () => {
	const { t, i18n } = useTranslation()
	const [visibleSections, setVisibleSections] = useState<Set<string>>(new Set())

	useEffect(() => {
		const observer = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					if (entry.isIntersecting) {
						setVisibleSections((prev) => new Set(prev).add(entry.target.id))
					}
				})
			},
			{
				threshold: 0.1,
				rootMargin: '0px 0px -100px 0px',
			},
		)

		const sections = document.querySelectorAll('[data-observe]')
		sections.forEach((section) => observer.observe(section))

		return () => observer.disconnect()
	}, [])

	return (
		<div className="min-h-screen bg-gradient-to-br from-gray-900 via-indigo-900/20 to-gray-900 text-white overflow-x-hidden">
			{/* 서브 비주얼 - 구글 지도 */}
			<SubVisual
				title=""
				subtitle=""
				height="h-[75vh]"
				overlayOpacity={0.4}
			>
				<div className="relative w-full h-full">
					<iframe
						src={
							i18n.language === 'ja' || i18n.language === 'ja-JP'
								? 'https://www.google.com/maps?q=35.677843684869266,139.78166121381648&z=17&output=embed&hl=ja'
								: 'https://www.google.com/maps?q=서울시+서초구+강남대로37길+26&z=17&output=embed&hl=ko'
						}
						width="100%"
						height="100%"
						style={{ border: 0 }}
						allowFullScreen={false}
						loading="lazy"
						referrerPolicy="no-referrer-when-downgrade"
						title={
							i18n.language === 'ja' || i18n.language === 'ja-JP'
								? 'MindCline オフィス所在地'
								: 'MindCline 오피스 위치'
						}
						className="absolute inset-0"
					/>

					{/* Location Info Card */}
					<div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 pointer-events-none">
						<div className="bg-gradient-to-br from-slate-900/95 to-indigo-900/95 backdrop-blur-xl px-8 py-4 rounded-2xl border border-indigo-400/30 shadow-2xl">
							<div className="flex items-center gap-3">
								<div className="w-3 h-3 bg-indigo-400 rounded-full animate-pulse" />
								<div>
									<p className="text-white font-bold text-lg">MindCline</p>
									<p className="text-indigo-200 text-sm">{t('page.contact.address')}</p>
								</div>
							</div>
						</div>
					</div>
				</div>
			</SubVisual>

			{/* Company Information Section */}
			<section
				id="company-info"
				data-observe
				className={`relative py-32 px-6 lg:px-12 transition-all duration-1000 ${
					visibleSections.has('company-info')
						? 'opacity-100 translate-y-0'
						: 'opacity-0 translate-y-20'
				}`}
			>
				<div className="absolute inset-0 bg-gradient-to-b from-transparent via-indigo-950/20 to-transparent" />
				<div className="absolute inset-0 overflow-hidden">
					<div className="absolute top-1/4 -left-48 w-96 h-96 bg-purple-600/10 rounded-full blur-3xl animate-pulse" />
					<div
						className="absolute bottom-1/4 -right-48 w-96 h-96 bg-blue-600/10 rounded-full blur-3xl animate-pulse"
						style={{ animationDelay: '1s' }}
					/>
				</div>

				<div className="container mx-auto relative z-10">
					<div className="text-center mb-20">
						<div className="inline-flex items-center px-6 py-3 bg-indigo-500/10 rounded-full border border-indigo-400/20 mb-6">
							<span className="text-indigo-300 text-sm font-medium tracking-wide">
								{t('page.contact.txt1')}
							</span>
						</div>
						<h2 className="text-4xl lg:text-5xl font-bold mb-6 bg-gradient-to-r from-white via-indigo-200 to-purple-200 text-transparent bg-clip-text">
							{t('page.contact.txt2')}
						</h2>
						<p className="text-lg text-gray-300 max-w-2xl mx-auto">
							{t('page.contact.txt3')}
						</p>
					</div>

					<div
						className={`grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto ${
							i18n.language === 'ja' || i18n.language === 'ja-JP'
								? 'lg:grid-cols-3'
								: 'lg:grid-cols-4'
						}`}
					>
						{[
							{
								icon: (
									<svg
										width="32"
										height="32"
										viewBox="0 0 32 32"
										fill="none"
									>
										<path
											d="M16 4C11.6 4 8 7.6 8 12C8 18 16 28 16 28C16 28 24 18 24 12C24 7.6 20.4 4 16 4Z"
											stroke="currentColor"
											strokeWidth="2"
											fill="none"
										/>
										<circle
											cx="16"
											cy="12"
											r="3"
											fill="currentColor"
										/>
									</svg>
								),
								title: t('page.contact.txt4'),
								content: [t('page.contact.txt5')],
								color: 'from-blue-500 to-cyan-500',
							},
							{
								icon: (
									<svg
										width="32"
										height="32"
										viewBox="0 0 32 32"
										fill="none"
									>
										<path
											d="M8 6C6.89543 6 6 6.89543 6 8V24C6 25.1046 6.89543 26 8 26H24C25.1046 26 26 25.1046 26 24V8C26 6.89543 25.1046 6 24 6H8Z"
											stroke="currentColor"
											strokeWidth="2"
											fill="none"
										/>
										<path
											d="M6 10L16 16L26 10"
											stroke="currentColor"
											strokeWidth="2"
											strokeLinecap="round"
										/>
									</svg>
								),
								title: t('page.contact.txt6'),
								content: [t('page.contact.txt7')],
								color: 'from-purple-500 to-pink-500',
							},
							{
								icon: (
									<svg
										width="32"
										height="32"
										viewBox="0 0 32 32"
										fill="none"
									>
										<path
											d="M8 4C6.89543 4 6 4.89543 6 6V26C6 27.1046 6.89543 28 8 28C9.10457 28 10 27.1046 10 26V6C10 4.89543 9.10457 4 8 4Z"
											fill="currentColor"
										/>
										<path
											d="M15 10C13.8954 10 13 10.8954 13 12V26C13 27.1046 13.8954 28 15 28C16.1046 28 17 27.1046 17 26V12C17 10.8954 16.1046 10 15 10Z"
											fill="currentColor"
										/>
										<path
											d="M22 16C20.8954 16 20 16.8954 20 18V26C20 27.1046 20.8954 28 22 28C23.1046 28 24 27.1046 24 26V18C24 16.8954 23.1046 16 22 16Z"
											fill="currentColor"
										/>
									</svg>
								),
								title: t('page.contact.txt8'),
								content: [t('page.contact.txt9')],
								color: 'from-emerald-500 to-teal-500',
								hideOnJa: true,
							},
							{
								icon: (
									<svg
										width="32"
										height="32"
										viewBox="0 0 32 32"
										fill="none"
									>
										<rect
											x="6"
											y="10"
											width="20"
											height="16"
											rx="2"
											stroke="currentColor"
											strokeWidth="2"
											fill="none"
										/>
										<path
											d="M10 10V8C10 6.89543 10.8954 6 12 6H20C21.1046 6 22 6.89543 22 8V10"
											stroke="currentColor"
											strokeWidth="2"
										/>
										<circle
											cx="16"
											cy="18"
											r="2"
											fill="currentColor"
										/>
									</svg>
								),
								title: t('page.contact.txt10'),
								content: [t('page.contact.txt11')],
								color: 'from-orange-500 to-red-500',
							},
						]
							.filter(
								(item) =>
									!item.hideOnJa ||
									(i18n.language !== 'ja' && i18n.language !== 'ja-JP'),
							)
							.map((item, idx) => (
								<div
									key={idx}
									data-observe
									className={`group relative bg-gradient-to-br from-slate-800/50 to-slate-900/50 p-8 rounded-2xl border border-slate-700/50 hover:border-slate-600 transition-all duration-500 hover:transform hover:scale-105 ${
										visibleSections.has('company-info')
											? 'opacity-100 translate-y-0'
											: 'opacity-0 translate-y-10'
									}`}
									style={{
										transitionDelay: `${idx * 100}ms`,
									}}
								>
									<div
										className={`absolute inset-0 bg-gradient-to-br ${item.color} opacity-0 group-hover:opacity-10 rounded-2xl transition-opacity duration-300`}
									/>
									<div className="relative z-10">
										<div
											className={`mb-6 text-indigo-400 group-hover:text-indigo-300 transition-colors duration-300`}
										>
											{item.icon}
										</div>
										<h3 className="text-xl font-bold mb-4 text-white">{item.title}</h3>
										{item.content.map((line, lineIdx) => (
											<p
												key={lineIdx}
												className="text-gray-300 text-sm leading-relaxed"
											>
												{line}
											</p>
										))}
									</div>
								</div>
							))}
					</div>
				</div>
			</section>
		</div>
	)
}

export default Contact
